import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { createTransporteurForExpediteur, getAvailableTransporteursForExpediteur } from '../../services/missionService';
import { toast } from 'react-toastify';
import { Building2, Mail, Phone, Truck, MapPin, Package, RefreshCcw, Plus } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';

const toList = (value) =>
  (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const AddTransporteur = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({
    enterpriseName: '',
    country: '',
    city: '',
    contactEmail: '',
    telephone: '',
    truckTypes: '',
    operatingZones: '',
    cargoTypes: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transporteurs, setTransporteurs] = useState([]);

  const loadTransporteurs = async () => {
    try {
      setLoading(true);
      const data = await getAvailableTransporteursForExpediteur();
      setTransporteurs(Array.isArray(data) ? data : []);
    } catch {
      setTransporteurs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransporteurs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await createTransporteurForExpediteur({
        enterpriseName: form.enterpriseName.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        contactEmail: form.contactEmail.trim(),
        telephone: form.telephone.trim(),
        truckTypes: toList(form.truckTypes),
        operatingZones: toList(form.operatingZones),
        cargoTypes: toList(form.cargoTypes),
      });
      toast.success(t('transporteurPartner.successAdd'));
      setForm({ enterpriseName: '', country: '', city: '', contactEmail: '', telephone: '', truckTypes: '', operatingZones: '', cargoTypes: '' });
      loadTransporteurs();
    } catch (err) {
      toast.error(err.message || t('transporteurPartner.errorAdd'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="row g-4">

          {/* FORM */}
          <div className="col-12 col-lg-5">
            <div className="card shadow-sm rounded-4">
              <div className="card-body">
                <h5 className="fw-semibold mb-4 d-flex align-items-center gap-2">
                  <Plus size={18} /> {t('transporteurPartner.title')}
                </h5>

                <form onSubmit={handleSubmit} className="vstack gap-3">
                  <div className="form-floating">
                    <input name="enterpriseName" value={form.enterpriseName} onChange={handleChange} required className="form-control" placeholder={t('transporteurPartner.form.companyName')} />
                    <label><Building2 size={14} className="me-1" /> {t('transporteurPartner.form.companyName')}</label>
                  </div>

                  <div className="row g-3">
                    <div className="col-6 form-floating">
                      <input name="country" value={form.country} onChange={handleChange} className="form-control" placeholder={t('client.country')} />
                      <label><MapPin size={14} className="me-1" /> {t('client.country')}</label>
                    </div>
                    <div className="col-6 form-floating">
                      <input name="city" value={form.city} onChange={handleChange} className="form-control" placeholder={t('expediteur.address.city')} />
                      <label>{t('expediteur.address.city')}</label>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-6 form-floating">
                      <input name="contactEmail" type="email" value={form.contactEmail} onChange={handleChange} required className="form-control" placeholder={t('common.email')} />
                      <label><Mail size={14} className="me-1" /> {t('common.email')}</label>
                    </div>
                    <div className="col-6 form-floating">
                      <input name="telephone" value={form.telephone} onChange={handleChange} className="form-control" placeholder={t('common.telephone')} />
                      <label><Phone size={14} className="me-1" /> {t('common.telephone')}</label>
                    </div>
                  </div>

                  <div className="form-floating">
                    <input name="truckTypes" value={form.truckTypes} onChange={handleChange} className="form-control" placeholder={t('transporteurPartner.form.truckTypes')} />
                    <label><Truck size={14} className="me-1" /> {t('transporteurPartner.form.truckTypes')}</label>
                  </div>

                  <div className="form-floating">
                    <input name="operatingZones" value={form.operatingZones} onChange={handleChange} className="form-control" placeholder={t('transporteurPartner.form.operatingZones')} />
                    <label><MapPin size={14} className="me-1" /> {t('transporteurPartner.form.operatingZones')}</label>
                  </div>

                  <div className="form-floating">
                    <input name="cargoTypes" value={form.cargoTypes} onChange={handleChange} className="form-control" placeholder={t('transporteurPartner.form.cargoTypes')} />
                    <label><Package size={14} className="me-1" /> {t('transporteurPartner.form.cargoTypes')}</label>
                  </div>

                  <button className="btn btn-primary w-100" disabled={saving}>
                    {saving ? t('common.saving') : t('transporteurPartner.form.savePartner')}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* LIST */}
          <div className="col-12 col-lg-7">
            <div className="card shadow-sm rounded-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0">{t('transporteurPartner.myTransporteurs')}</h5>
                  <button className="btn btn-outline-secondary btn-sm" onClick={loadTransporteurs} disabled={loading}>
                    <RefreshCcw size={14} className={loading ? 'spin' : ''} />
                  </button>
                </div>

                {transporteurs.length === 0 ? (
                  <div className="text-muted text-center py-4">{t('transporteurPartner.noTransporteurs')}</div>
                ) : (
                  <div className="vstack gap-2">
                    {transporteurs.map((t) => (
                      <div key={t.id} className="border rounded-3 p-3">
                        <div className="fw-semibold">{t.enterpriseName}</div>
                        <div className="text-muted small">{[t.city, t.country].filter(Boolean).join(', ')}</div>
                        <div className="small">{t.contactEmail} {t.telephone && `· ${t.telephone}`}</div>
                        <div className="mt-2 d-flex flex-wrap gap-1">
                          {t.truckTypes?.map((x) => <span key={x} className="badge bg-light text-dark">🚚 {x}</span>)}
                          {t.operatingZones?.map((x) => <span key={x} className="badge bg-light text-dark">📍 {x}</span>)}
                          {t.cargoTypes?.map((x) => <span key={x} className="badge bg-light text-dark">📦 {x}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddTransporteur;
